import { CleansingInstanceLike, CleansingTypeUsageLike } from "./models/decontamination/cleansingTypeusage";
import { Order, OrderAssignmentLike, OrderLike } from "./models/decontamination/order";
import { OrderItemLike, OrderItemAssignmentLike } from "./models/decontamination/orderItem";
import { MeasurementLike } from "./models/decontamination/measurement";
import { CleansingType, Reason, Facility, OrderImage, MeasuringTypeLike } from "./models/decontamination/index";
import { BaseDatabase, DexieTable } from "../../core/db/index";
import { ForeignKey } from "../../core/db/models";
import { CustomerLike } from "./models/customer";
import { Business, DeliveryNote, DeliveryNoteItem, TransportationUnit, Package, Reservation, DeliveryLocation } from "./models/index";


export default class FortumDatabase extends BaseDatabase
{
    // Decontamination
    public Business: Dexie.Table<Business, number>;
    public DeconOrder: Dexie.Table<OrderLike, number>;
    public DeconOrderItem: Dexie.Table<OrderItemLike, number>;
    public DeconMeasurement: Dexie.Table<MeasurementLike, number>;
    public DeconCleansingTypeUsage: Dexie.Table<CleansingTypeUsageLike, number>;
    public DeconCleansingInstance: Dexie.Table<CleansingInstanceLike, number>;
    public DeconCleansingType: Dexie.Table<CleansingType, number>;
    public DeconMeasuringType: Dexie.Table<MeasuringTypeLike, number>;
    public DeconReason: Dexie.Table<Reason, number>;
    public DeconFacility: Dexie.Table<Facility, number>;
    public DeconOrderImage: Dexie.Table<OrderImage, number>;
    public DeconOrderAssignment: Dexie.Table<OrderAssignmentLike, number>;
    public DeconOrderItemAssignment: Dexie.Table<OrderItemAssignmentLike, number>;

    // Global
    public Customer: Dexie.Table<CustomerLike, number>;

    // Package tracking?
    public DeliveryNote: Dexie.Table<DeliveryNote, string>;
    public DeliveryNoteItem: Dexie.Table<DeliveryNoteItem, number>;
    public TransportationUnit: Dexie.Table<TransportationUnit, number>;
    public Package: Dexie.Table<Package, number>;

    // Region reservations
    public Reservation: Dexie.Table<Reservation, number>;
    public DeliveryLocation: Dexie.Table<DeliveryLocation, number>;

    constructor(name: string)
    {
        super(name);

        // Initial app version: Decontamination implementation
        this.version(2).stores({
            Business: 'id,ext_id',
            DeconOrder: 'id,ext_id,uuid',
            DeconOrderItem: 'id,order_id',
            DeconMeasurement: 'id,item_id,cleansing_instance_id,type_id',
            DeconCleansingInstance: 'id,item_id',
            DeconCleansingTypeUsage: 'id,instance_id,type_id',
            DeconCleansingType: 'id,ext_id',
            DeconMeasuringType: 'id,ext_id',
            DeconReason: 'id,ext_id',
            DeconFacility: 'id,ext_id',
            DeconOrderImage: 'id,order_id',
            Customer: 'id',
        });

        // Initial package tracking implementation
        this.version(3).stores({
            DeliveryNote: 'id',
            DeliveryNoteItem: 'id, delivery_note_id'
        });

        // Decontamination update: Add order and order item assignments
        this.version(4).stores({
            DeconOrderAssignment: 'id,order_id,user_id',
            DeconOrderItemAssignment: 'id,item_id,user_id'
        });

        // Package tracking update: Add transportation unit
        this.version(5).stores({
            TransportationUnit: 'id'
        });

        // Package tracking update: Add package
        this.version(6).stores({
            Package: 'id, delivery_user_id'
        });

        // Region Reservations update: Add Reservations
        this.version(7).stores({
            Reservation: 'id'
        });

        // Region Reservations update: Add DeliveryLocation
        this.version(8).stores({
            DeliveryLocation: 'id'
        });
        
        // Add is_offline indexed field to all Decontamination models
        // Allow creating records offline by auto-incrementing pk
        this.version(9).stores({
            DeconOrder: 'id,ext_id,uuid,is_offline',
            DeconOrderItem: '++id,order_id,is_offline',
            DeconMeasurement: '++id,item_id,cleansing_instance_id,type_id,is_offline',
            DeconCleansingInstance: '++id,item_id,is_offline',
            DeconCleansingTypeUsage: '++id,instance_id,type_id,is_offline',
            DeconCleansingType: 'id,ext_id,is_offline',
            DeconMeasuringType: 'id,ext_id,is_offline',
            DeconReason: 'id,ext_id,is_offline',
            DeconFacility: 'id,ext_id,is_offline',
            DeconOrderImage: '++id,order_id,is_offline',
            DeconOrderAssignment: '++id,order_id,user_id,is_offline',
            DeconOrderItemAssignment: '++id,item_id,user_id,is_offline',
        }).upgrade((transaction) => {
            for (const tableName of [
                    'DeconOrder', 'DeconOrderItem', 'DeconMeasurement', 'DeconCleansingInstance', 'DeconCleansingTypeUsage', 
                    'DeconCleansingType', 'DeconMeasuringType', 'DeconReason', 'DeconFacility', 'DeconOrderImage', 
                    'DeconOrderAssignment', 'DeconOrderItemAssignment']) {
                transaction.table(tableName).toCollection().modify({is_offline: 0});
            };
        });
    }
}
